import { Heart, Shield, Sparkles, UserCheck, HandHeart, Users, BadgeCheck, GraduationCap } from "lucide-react";
import SEO from "../components/SEO";
import { COMPANY, IMAGES } from "../lib/constants";

const values = [
  { icon: Heart, title: "Compassion", desc: "Treating every family with empathy and warmth." },
  { icon: Shield, title: "Integrity", desc: "Doing the right thing — every time, even when no one is watching." },
  { icon: Sparkles, title: "Dignity", desc: "Honoring each person's voice, choices, and story." },
  { icon: UserCheck, title: "Clinical Excellence", desc: "Evidence-based care, led by experienced RNs." },
  { icon: HandHeart, title: "Personalized Care", desc: "Plans built around every individual's needs." },
  { icon: Users, title: "Collaboration", desc: "Partnering closely with families and physicians." },
  { icon: BadgeCheck, title: "Safety", desc: "Rigorous standards in every visit, every shift." },
];

export default function About() {
  return (
    <>
      <SEO
        title="About 1st Home Care Services | Licensed Home Care in Northern Virginia"
        description="Meet the team behind 1st Home Care Services. Led by Administrator and Director of Nursing Betelihem Bekalo, RN, MSN. Our mission, vision, and values."
        canonical="https://1sthomecare.com/about"
      />

      <section className="container-pad section-pad">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
            About Us
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-blue-dark text-balance">
            Compassionate, family-centered home care for Northern Virginia.
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            {COMPANY.name} (dba 1st Home Care) is a licensed Virginia home care
            organization providing skilled nursing, private duty nursing, personal
            care, and respite care. We're proud to serve seniors, children, and
            adults across Springfield, Fairfax, Arlington, Alexandria, and Falls
            Church — bringing trusted, hospital-grade care into the comfort of home.
          </p>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section data-testid="leadership-section" className="container-pad pb-16 md:pb-24">
        <div className="grid lg:grid-cols-5 gap-10 items-stretch">
          <div className="lg:col-span-2 relative rounded-3xl overflow-hidden shadow-xl">
            <img
              src={IMAGES.leadership}
              alt="Experienced RN leadership at 1st Home Care Services"
              className="w-full h-full object-cover min-h-[420px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-blue-dark/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <div className="text-xs font-semibold uppercase tracking-widest text-brand-amber-light">
                Leadership
              </div>
              <div className="font-heading text-2xl font-bold mt-1">Led by an Experienced Nurse</div>
              <div className="text-sm opacity-90">Administrator / Director of Nursing</div>
            </div>
          </div>

          <div className="lg:col-span-3 bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-10">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-brand-blue-dark tracking-tight">
              Led by an experienced nurse you can trust.
            </h2>
            <p className="mt-4 text-gray-600 leading-relaxed">
              Our leadership team brings deep clinical experience to every care
              plan we build. As both Administrator and Director of Nursing, our
              lead RN ensures that every caregiver, nurse, and visit upholds the
              highest standards of safety, compassion, and clinical excellence.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
              {[
                "Licensed RN with a Master of Science in Nursing",
                "Decades of combined clinical & home-care experience",
                "Personally reviews every new care plan",
                "Hands-on with caregiver training & quality",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <BadgeCheck className="w-4 h-4 text-brand-amber mt-0.5 shrink-0" />
                  <span className="text-gray-700">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section data-testid="mission-vision" className="bg-brand-gray/50 section-pad">
        <div className="container-pad grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100 shadow-sm">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
              Our Mission
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-brand-blue-dark">
              Enhance quality of life through compassionate, cost-effective,
              family-centered care at home.
            </h3>
          </div>
          <div className="bg-brand-blue text-white rounded-3xl p-8 md:p-10 shadow-sm">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber-light mb-3">
              Our Vision
            </div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold">
              Be a trusted leader in home healthcare through excellence,
              compassion, and innovation.
            </h3>
          </div>
        </div>
      </section>

      {/* CAREGIVER DEVELOPMENT & TRAINING */}
      <section data-testid="development-programs" className="container-pad pb-16 md:pb-24">
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-12">
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-1">
              <div className="w-14 h-14 rounded-2xl bg-brand-amber/10 text-brand-amber-dark flex items-center justify-center mb-4">
                <GraduationCap className="w-7 h-7" />
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-2">
                For Development Programs
              </div>
              <h2 className="font-heading text-3xl font-semibold text-brand-blue-dark tracking-tight">
                Caregiver development &amp; training
              </h2>
              <p className="mt-4 text-gray-600 leading-relaxed">
                We invest in our team so your family gets the best possible care.
                Every caregiver is trained, supervised, and supported.
              </p>
            </div>
            <ul className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
              {[
                "In-service training available",
                "Initial competency & skills assessment",
                "Annual continuing education",
                "Direct supervision by our Director of Nursing",
                "Specialty training (vent/trach, pediatric, dementia)",
                "Background-checked & credentialed staff",
              ].map((p) => (
                <li
                  key={p}
                  data-testid={`development-bullet-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                  className="flex items-start gap-2 bg-brand-gray/50 rounded-xl px-4 py-3"
                >
                  <BadgeCheck className="w-4 h-4 text-brand-amber mt-0.5 shrink-0" />
                  <span className="text-sm text-gray-700">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section data-testid="values-section" className="container-pad section-pad">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
            What We Stand For
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-blue-dark tracking-tight">
            Our Core Values
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((v) => (
            <div
              key={v.title}
              data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-brand-amber/10 text-brand-amber-dark flex items-center justify-center mb-4">
                <v.icon className="w-5 h-5" />
              </div>
              <div className="font-heading font-semibold text-brand-blue-dark">{v.title}</div>
              <div className="text-sm text-gray-600 mt-1">{v.desc}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
